import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
    makeStyles,
    useTheme,
    Button,
    IconButton,
    Tooltip,
} from '@material-ui/core'
import { GetApp as DownloadIcon } from '@material-ui/icons'

import { _useFinderSelection, useCompany, useUser } from '../../../hooks'
import { UpdateWorkOrderRequest, WorkorderResponse } from '../../../store'
import { downloadComprehensiveWorkorderReport } from '../../../models/WorkOrder/services'
import { format, isToday } from 'date-fns'
import { VendorCell } from './VendorCell'
import { VendorHeaderCell } from './VendorHeaderCell'
import { DateTime } from 'luxon'
import { useTimezone } from '../../../contexts/timezone'

import {
    ListVendor,
    User,
    Service,
    Schedule,
    Inspection,
    InspectionDataResponse,
    ModelMap,
    WorkorderType,
} from '../../../models'
import { resolveRoute, Routes } from '../../../helpers'
import { UserHeaderCell } from './UserHeaderCell'
import { getInspectionListFromUnitInspectionRequest } from '../../../hooks/useInspectionData'
import { InspectionCell } from './InspectionCell'
import { JbDrawerId, MY_TEAM } from '../types'
import { workorderContext } from '../../../contexts'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    filteredWorkorderList: WorkorderResponse[]
    workorderList: WorkorderResponse[]
    userList: User[]
    vendorList: ListVendor[]
    serviceList: Service[]
    workorderMap: Map<string, WorkorderResponse[]>
    vendorFilter: number
    sortedVendors: ListVendor[]
    dateRange: Date[]
    scheduleList: Schedule[]
    analyticData: any
    isMultiselectMode: boolean
    selectedWorkorders: ModelMap<WorkorderResponse>
    inspectionData?: InspectionDataResponse
    inspectionList?: Inspection[]
    sortedUsers?: User[]
    organizationView?: boolean
    setSelectedWorkorders: React.Dispatch<
        React.SetStateAction<ModelMap<WorkorderResponse>>
    >
    openDetailDrawer: (
        workorder: WorkorderResponse,
        drawerId: JbDrawerId,
    ) => void
    getInspectionListFromUnitInspection?: (
        request: getInspectionListFromUnitInspectionRequest,
    ) => Promise<Inspection[]>
}

const useStyles = makeStyles((theme) => ({
    jobBoardContainer: {
        height: '100%',
        overflow: 'auto',
        position: 'relative',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 'max-content',
    },
    headerRow: {
        display: 'flex',
        position: 'sticky',
        top: 0,
        zIndex: 3,
        backgroundColor: theme.palette.background.paper,
    },
    dateColumn: {
        position: 'sticky',
        left: 0,
        zIndex: 2,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
    },
    dateHeaderCell: {
        padding: theme.spacing(1, 1, 0.5, 1),
        minWidth: 120,
        maxWidth: 120,
        fontWeight: 700,
    },
    dateCell: {
        padding: theme.spacing(0.5, 1),
        minWidth: 120,
        maxWidth: 120,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    dateText: {
        ...(typographyStyle(theme.typography.subtitle1) as Record<
            string,
            unknown
        >),
        fontWeight: 700,
    },
    workorderCount: {
        ...(typographyStyle(theme.typography.caption) as Record<
            string,
            unknown
        >),
        color: theme.palette.text.secondary,
    },
    todayCell: {
        backgroundColor: theme.palette.primary.light,
        borderBottom: `2px solid ${theme.palette.primary.main}`,
    },
    vendorHeaderCell: {
        padding: theme.spacing(0, 1),
        maxWidth: 300,
        minWidth: 300,
        marginRight: theme.spacing(2),
    },
    workorderCell: {
        padding: theme.spacing(0, 1),
        minWidth: 300,
        maxWidth: 300,
        marginRight: theme.spacing(2),
    },
    row: {
        display: 'flex',
    },
    tabStyle: {
        ...(typographyStyle(theme.typography.subtitle1) as Record<
            string,
            unknown
        >),
        fontWeight: 700,
        textTransform: 'none',
    },
    dragging: {
        border: `1px solid ${theme.palette.primary.dark}`, // Use a solid border
        boxShadow: `0 0 10px ${theme.palette.primary.light}`, // Optional: add a shadow for emphasis
    },
}))

export const JobBoard = (props: Props) => {
    const {
        filteredWorkorderList,
        workorderList,
        userList,
        sortedUsers,
        vendorList,
        workorderMap,
        vendorFilter,
        sortedVendors,
        serviceList,
        dateRange,
        scheduleList,
        inspectionData,
        inspectionList,
        analyticData,
        isMultiselectMode,
        selectedWorkorders,
        organizationView,
        setSelectedWorkorders,
        getInspectionListFromUnitInspection,
        openDetailDrawer,
    } = props

    const classes = useStyles()
    const theme = useTheme()
    const history = useHistory()

    const [
        draggedWorkorder,
        setDraggedWorkorder,
    ] = useState<WorkorderResponse | null>(null)

    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const scrollIntervalRef = useRef<number | null>(null)

    const { workspaceUser } = useUser()
    const { currentTimezone } = useTimezone()

    const { updateVendorServices } = useCompany()

    const {
        updateWorkorder,
        updateWorkorderState,
        transitionWorkorder,
        deleteWorkorder,
        updateWoWithNextAvailableDate,
        getServiceRequestChannel,
    } = workorderContext()

    useEffect(() => {
        // Clean up the interval on component unmount
        return () => {
            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current)
            }
        }
    }, [])

    const handleDragStart = (e: React.DragEvent, workorderId: string) => {
        const workorder = filteredWorkorderList.find(
            (wo) => wo.id.toString() === workorderId,
        )
        if (workorder && !workorder.price_locked) {
            e.dataTransfer.setData('text/plain', workorderId)
            setDraggedWorkorder(workorder)
            startAutoScroll(e)
        } else {
            e.preventDefault()
        }
    }

    const startAutoScroll = (
        e: React.DragEvent,
        columnElement?: HTMLElement | null,
    ) => {
        // Clear any existing interval
        if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current)
        }

        scrollIntervalRef.current = window.setInterval(() => {
            if (scrollContainerRef.current) {
                const container = scrollContainerRef.current
                const containerRect = container.getBoundingClientRect()
                const horizontalScrollSpeed = 5
                const verticalScrollSpeed = 5

                // Horizontal scrolling
                if (e.clientX > containerRect.right - 50) {
                    container.scrollLeft += horizontalScrollSpeed
                } else if (e.clientX < containerRect.left + 50) {
                    container.scrollLeft -= horizontalScrollSpeed
                }

                // Vertical scrolling
                if (columnElement) {
                    const columnRect = columnElement.getBoundingClientRect()
                    if (e.clientY > columnRect.bottom - 50) {
                        columnElement.scrollTop += verticalScrollSpeed
                    } else if (e.clientY < columnRect.top + 50) {
                        columnElement.scrollTop -= verticalScrollSpeed
                    }
                }
            }
        }, 16) // ~60fps
    }

    const stopAutoScroll = () => {
        if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current)
            scrollIntervalRef.current = null
        }
    }

    const handleDropOnVendor = (workorderId: string, newVendorId: number) => {
        const workorder = workorderList?.find(
            (wo) => wo.id.toString() === workorderId,
        )

        let serviceValid = false
        let newVendor: ListVendor | null = null

        newVendor = vendorList.find((v) => v.id === newVendorId) ?? null

        if (newVendorId === -1) {
            serviceValid = true
        } else {
            serviceValid =
                newVendor?.services.some(
                    (service) => service.id === workorder?.service_id,
                ) ?? false
        }

        if (workorder && !workorder.price_locked && serviceValid) {
            updateWoWithNextAvailableDate(workorder, newVendorId).catch(() =>
                updateWorkorderState(workorder),
            )
        }
    }

    const handleDrop = (
        workorderId: string,
        newId: number,
        newDateKey: string,
        isUser?: boolean,
    ) => {
        const workorder = workorderList?.find(
            (wo) => wo.id.toString() === workorderId,
        )

        let serviceValid = false
        let newVendor: ListVendor | null = null
        let newUser: User | null = null

        if (isUser) {
            newUser = userList.find((u) => u.id === newId) ?? null
            serviceValid = true
        } else {
            newVendor = vendorList.find((v) => v.id === newId) ?? null

            if (newId === -1) {
                serviceValid = true
            } else {
                serviceValid =
                    newVendor?.services.some(
                        (service) => service.id === workorder?.service_id,
                    ) ?? false
            }
        }

        if (workorder && !workorder.price_locked && serviceValid) {
            let updatedWorkorder = { ...workorder }
            const request: UpdateWorkOrderRequest = {
                workorderId: workorder.id,
                body: {},
            }

            const originalStart = DateTime.fromISO(
                workorder.start_date,
            ).setZone(currentTimezone)
            const originalEnd = DateTime.fromISO(workorder.end_date).setZone(
                currentTimezone,
            )

            const sameOriginalDate = originalStart.hasSame(originalEnd, 'day')

            const [year, month, day] = newDateKey.split('-').map(Number)

            const newStart = originalStart.set({ year, month, day })
            const daysDifference = newStart
                .startOf('day')
                .diff(originalStart.startOf('day'), 'days').days

            const newEnd = sameOriginalDate
                ? originalEnd.set({ year, month, day })
                : originalEnd.plus({ days: daysDifference })

            const newStartIso = newStart.toUTC().toISO()!
            const newEndIso = newEnd.toUTC().toISO()!

            updatedWorkorder = {
                ...updatedWorkorder,
                start_date: newStartIso,
                end_date: newEndIso,
            }

            request.body = {
                start_date: newStartIso,
                end_date: newEndIso,
            }

            if (newVendor) {
                updatedWorkorder.vendor_id = newVendor.id
                updatedWorkorder.user_id = null
                request.body.assign_to_id = newVendor.id
                request.body.assign_to_type = 'vendor'
            } else if (newUser) {
                updatedWorkorder.user_id = newUser.id
                updatedWorkorder.vendor_id = null
                request.body.assign_to_id = newUser.id
                request.body.assign_to_type = 'user'
            }

            // Update the workorder in your our local state
            updateWorkorderState(updatedWorkorder)

            // Update workorder on the backend
            updateWorkorder(request).catch(() =>
                updateWorkorderState(workorder),
            )
        }

        setDraggedWorkorder(null)
        stopAutoScroll()
    }

    const handleDragOver = (
        e: React.DragEvent,
        columnElement: HTMLElement | null,
    ) => {
        e.preventDefault()
        startAutoScroll(e, columnElement)
    }

    const handleDragEnd = () => {
        setDraggedWorkorder(null)
        stopAutoScroll()
    }

    const navigateToScheduleDetail = (scheduleId: number) => {
        const project = scheduleList.find(
            (project) => project.id === scheduleId,
        )

        let route = Routes.apartmentScheduleDetail
        if (project && !project.active) {
            route = Routes.apartmentScheduleArchivedDetail
        }

        history.push(resolveRoute(route, ':id', scheduleId))
    }

    const navigateToInspectionDetail = (inspectionId: number) => {
        const route = Routes.inspectionDetail

        const url = resolveRoute(route, ':id', inspectionId)

        window.open(url, '_blank')
    }
    const handleSelectWorkorder = (workorder: WorkorderResponse) => {
        setSelectedWorkorders((prev) => {
            const newMap = { ...prev }
            if (workorder.id in newMap) {
                delete newMap[workorder.id]
            } else {
                newMap[workorder.id] = workorder
            }
            return newMap
        })
    }

    const onClickServiceRequest = (workorder: WorkorderResponse) => {
        if (
            workorder.type === WorkorderType.SERVICE_REQUEST &&
            workorder.service_request
        ) {
            getServiceRequestChannel(workorder).then((channel) => {
                if (workorder.service_request) {
                    const updatedWorkorder = {
                        ...workorder,
                        service_request: {
                            ...workorder.service_request,
                            channel_id: channel,
                        },
                    }
                    openDetailDrawer(
                        updatedWorkorder,
                        JbDrawerId.serviceRequest,
                    )
                }
            })
        }
    }

    return (
        <div
            className={classes.jobBoardContainer}
            ref={scrollContainerRef}
            onDragEnd={handleDragEnd}
        >
            <div className={classes.tableContainer}>
                <div className={classes.headerRow}>
                    <div
                        className={`${classes.dateColumn} ${classes.dateHeaderCell}`}
                    >
                        Date
                    </div>
                    {vendorFilter !== MY_TEAM
                        ? sortedVendors.map((vendor) => (
                              <div
                                  key={`VENDOR_${vendor.id}`}
                                  className={classes.vendorHeaderCell}
                              >
                                  <VendorHeaderCell
                                      vendor={vendor}
                                      theme={theme}
                                      draggedWorkorder={draggedWorkorder}
                                      serviceList={serviceList}
                                      updateVendorServices={
                                          updateVendorServices
                                      }
                                      apartmentList={[]}
                                      onDrop={handleDropOnVendor}
                                  />
                              </div>
                          ))
                        : sortedUsers?.map((user) => (
                              <div
                                  key={`USER_${user.id}`}
                                  className={classes.vendorHeaderCell}
                              >
                                  <UserHeaderCell user={user} theme={theme} />
                              </div>
                          ))}
                </div>
                {dateRange.map((date) => {
                    const dateKey = format(date, 'yyyy-MM-dd')
                    const isDateToday = isToday(date)
                    const workorderCount =
                        workorderMap.get(dateKey)?.length ?? 0

                    return (
                        <div key={dateKey} className={classes.row}>
                            <div
                                className={`${classes.dateColumn} ${
                                    classes.dateCell
                                } ${isDateToday ? classes.todayCell : ''}`}
                            >
                                <span className={classes.dateText}>
                                    {format(date, 'EEE, MMM d')}
                                </span>
                                <span className={classes.workorderCount}>
                                    {workorderCount} workorder
                                    {workorderCount !== 1 ? 's' : ''}
                                </span>
                                {workorderCount > 0 && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                downloadComprehensiveWorkorderReport(
                                                    workorderMap.get(dateKey) ??
                                                        [],
                                                    `${dateKey} workorders`,
                                                )
                                            }
                                            style={{
                                                marginLeft: theme.spacing(1),
                                                minWidth: 'auto',
                                                padding: theme.spacing(0.5, 1),
                                            }}
                                        >
                                            <Tooltip title="Download Workorder Report">
                                                <DownloadIcon />
                                            </Tooltip>
                                        </IconButton>
                                    </div>
                                )}
                            </div>
                            {vendorFilter !== MY_TEAM
                                ? sortedVendors.map((vendor) => (
                                      <div
                                          key={`${dateKey}-${vendor.id}-VENDOR`}
                                          className={classes.workorderCell}
                                      >
                                          <VendorCell
                                              vendor={vendor}
                                              dateKey={dateKey}
                                              workorders={
                                                  workorderMap
                                                      .get(dateKey)
                                                      ?.filter(
                                                          (wo) =>
                                                              vendor.id ===
                                                              wo.vendor_id,
                                                      ) ?? []
                                              }
                                              theme={theme}
                                              workspaceUser={workspaceUser}
                                              transitionWorkorder={
                                                  transitionWorkorder
                                              }
                                              openDetailDrawer={
                                                  openDetailDrawer
                                              }
                                              onDragStart={handleDragStart}
                                              onDrop={handleDrop}
                                              onDragOver={handleDragOver}
                                              draggedWorkorder={
                                                  draggedWorkorder
                                              }
                                              isLastColumn={
                                                  vendor.id ===
                                                  sortedVendors[
                                                      sortedVendors.length - 1
                                                  ].id
                                              }
                                              navigateToScheduleDetail={
                                                  navigateToScheduleDetail
                                              }
                                              deleteWorkorder={deleteWorkorder}
                                              isMultiselectMode={
                                                  isMultiselectMode
                                              }
                                              selectedWorkorders={
                                                  selectedWorkorders
                                              }
                                              handleSelectWorkorder={
                                                  handleSelectWorkorder
                                              }
                                              organizationView={
                                                  organizationView
                                              }
                                              onClickServiceRequest={
                                                  onClickServiceRequest
                                              }
                                          />
                                      </div>
                                  ))
                                : sortedUsers?.map((user) => (
                                      <div
                                          key={`${dateKey}-${user.id}-USER`}
                                          className={classes.workorderCell}
                                      >
                                          {inspectionData && (
                                              <InspectionCell
                                                  inspectionData={
                                                      inspectionData
                                                  }
                                                  date={dateKey}
                                                  user={user}
                                                  theme={theme}
                                                  inspectionList={
                                                      inspectionList ?? []
                                                  }
                                                  analyticData={
                                                      analyticData ?? null
                                                  }
                                                  getInspectionListFromUnitInspection={
                                                      getInspectionListFromUnitInspection
                                                  }
                                                  navigateToInspectionDetail={
                                                      navigateToInspectionDetail
                                                  }
                                              />
                                          )}
                                          <VendorCell
                                              user={user}
                                              dateKey={dateKey}
                                              workorders={
                                                  workorderMap
                                                      .get(dateKey)
                                                      ?.filter(
                                                          (wo) =>
                                                              user.id ===
                                                              wo.user_id,
                                                      ) ?? []
                                              }
                                              theme={theme}
                                              workspaceUser={workspaceUser}
                                              transitionWorkorder={
                                                  transitionWorkorder
                                              }
                                              openDetailDrawer={
                                                  openDetailDrawer
                                              }
                                              onDragStart={handleDragStart}
                                              onDrop={handleDrop}
                                              onDragOver={handleDragOver}
                                              draggedWorkorder={
                                                  draggedWorkorder
                                              }
                                              isLastColumn={
                                                  user.id ===
                                                  sortedUsers[
                                                      sortedUsers.length - 1
                                                  ].id
                                              }
                                              navigateToScheduleDetail={
                                                  navigateToScheduleDetail
                                              }
                                              deleteWorkorder={deleteWorkorder}
                                              isMultiselectMode={
                                                  isMultiselectMode
                                              }
                                              selectedWorkorders={
                                                  selectedWorkorders
                                              }
                                              handleSelectWorkorder={
                                                  handleSelectWorkorder
                                              }
                                              organizationView={
                                                  organizationView
                                              }
                                              onClickServiceRequest={
                                                  onClickServiceRequest
                                              }
                                          />
                                      </div>
                                  ))}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
